import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  getGiftableProductKey,
  getGiftProductTargetForGiftableProduct,
  getGiftProductTargetKey,
  GIFTABLE_PRODUCT_GROUPS,
  GiftableProduct,
  GiftCenterCarouselImage,
} from '../../../../../../../../models/gift-center';
import { GiftProductType } from '../../../../../../../../models/gift';
import { CallbackResult, CallbackResultStatus } from '../../../../../../../../utils';
import { GiftService } from '../../../../../../../../services/gift/gift.service';

const MAX_IMAGE_FILE_BYTES = 4194304;


@Component({
  selector: 'app-configure-gift-center-carousel-image-modal',
  templateUrl: './configure-gift-center-carousel-image-modal.component.html',
  styleUrls: ['./configure-gift-center-carousel-image-modal.component.css']
})
export class ConfigureGiftCenterCarouselImageModalComponent implements OnInit {
  @Input() image: GiftCenterCarouselImage = null;
  @Input() giftableProducts: GiftableProduct[] = [];
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public isNewImage: boolean;

  public imageFileData: File = null;
  public imagePreview: string | null = null;
  public buttonText = '';
  public selectedProductKey: string | null = null;

  public serverError: number = null;
  public errorMessage = '';
  public errors: any = {};
  public savingImage = false;

  public readonly GIFTABLE_PRODUCT_GROUPS = GIFTABLE_PRODUCT_GROUPS;
  public readonly getGiftableProductKey = getGiftableProductKey;

  constructor(
    public activeModal: NgbActiveModal,
    private giftService: GiftService,
  ) {}

  ngOnInit() {
    this.isNewImage = this.image === null;
    if (!this.isNewImage) {
      this.imagePreview = this.image.image;
      this.buttonText = this.image.button_text;
      this.selectedProductKey = getGiftProductTargetKey(this.image);
    }
  }

  public productsOfType(productType: GiftProductType): GiftableProduct[] {
    return this.giftableProducts.filter(p => p.product_type === productType);
  }

  /** The image's saved target is no longer offered, so the select pins it. */
  public savedTargetNotGiftable(): boolean {
    if (this.isNewImage) {
      return false;
    }
    const savedKey = getGiftProductTargetKey(this.image);
    return savedKey !== null && !this.giftableProducts.some(p => getGiftableProductKey(p) === savedKey);
  }

  public savedTargetKey(): string | null {
    return this.isNewImage ? null : getGiftProductTargetKey(this.image);
  }

  public buttonIncomplete(): boolean {
    return (this.buttonText.trim() !== '') !== (this.selectedProductKey !== null);
  }

  public canSave(): boolean {
    return !this.savingImage
      && !this.buttonIncomplete()
      && !this.errors.image
      && (!this.isNewImage || this.imageFileData !== null);
  }

  public attachImageFile(event) {
    this.errors.image = '';
    const file = event.target.files[0] as File;
    if (!file) {
      this.discardChosenImage(event);
      return;
    }
    if (file.type.match(/image\/*/) == null) {
      this.errors.image = 'Invalid image file.';
      this.discardChosenImage(event);
      return;
    }
    if (file.size > MAX_IMAGE_FILE_BYTES) {
      this.errors.image = 'Image file is larger than 4 MB.';
      this.discardChosenImage(event);
      return;
    }

    this.imageFileData = file;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (this.imageFileData === file) {
        this.imagePreview = reader.result as string;
      }
    };
  }

  /** Forget any chosen file and show the existing image again, if there is one. */
  private discardChosenImage(event) {
    event.target.value = '';
    this.imageFileData = null;
    this.imagePreview = this.isNewImage ? null : this.image.image;
  }

  public saveImage() {
    if (!this.canSave()) {
      return;
    }
    this.savingImage = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<GiftCenterCarouselImage>;
    if (this.isNewImage) {
      savePromise = this.giftService.createGiftCenterCarouselImage(this.buildFormData());
    } else if (this.imageFileData !== null) {
      savePromise = this.giftService.updateGiftCenterCarouselImage(this.image.url, this.buildFormData());
    } else {
      savePromise = this.giftService.updateGiftCenterCarouselImage(this.image.url, this.buildJsonBody());
    }

    savePromise
      .then(() => {
        this.savedCallback({
          text: this.isNewImage ? 'Image added successfully.' : 'Image saved successfully.',
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to take this action.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.savingImage = false; });
  }

  /**
   * The button fields for the selected gift. Unchosen ids are sent as null
   * (empty in multipart, which DRF reads as null) so an edit clears them.
   */
  private buildJsonBody(): object {
    const product = this.selectedProduct();
    if (product === null) {
      if (this.selectedProductKey !== null) {
        return {button_text: this.buttonText.trim()};
      }
      return {
        button_text: '',
        product_type: '',
        membership_id: null,
        entry_pass_id: null,
        voucher_pack_id: null,
      };
    }
    return {
      button_text: this.buttonText.trim(),
      ...getGiftProductTargetForGiftableProduct(product),
    };
  }

  private buildFormData(): FormData {
    const formData = new FormData();
    if (this.imageFileData !== null) {
      formData.append('image', this.imageFileData, this.imageFileData.name);
    }
    const body = this.buildJsonBody();
    for (const key of Object.keys(body)) {
      const value = body[key];
      formData.append(key, value === null ? '' : value.toString());
    }
    return formData;
  }

  /**
   * The chosen giftable product, or null for "No button" and for an unchanged
   * target that is no longer giftable.
   */
  private selectedProduct(): GiftableProduct | null {
    if (this.selectedProductKey === null) {
      return null;
    }
    return this.giftableProducts.find(p => getGiftableProductKey(p) === this.selectedProductKey) || null;
  }
}
